<template>
  <div/>
</template>

<script>
import L from 'leaflet';
import 'leaflet.markercluster';

import { mapActions, mapGetters } from 'vuex';
import EventBus from '@/events/event-bus';
import MapMode from '@/enums/MapMode';
import GeoType from '@/enums/GeoType';
import * as dataCache from '@/store/cache/dataCache';

export default {
  name: 'ObjectsLayer',
  props: {
    map: {
      type: Object,
      required: true,
    },
    mapMode: {
      type: String,
      required: true,
      nullable: true,
    },
  },
  computed: {
    ...mapGetters({
      actionStatuses: 'settings/getActionStatuses',
      dynamicActionTypes: 'settings/getDynamicActionTypes',
      keyValues: 'settings/getKeyValues',
      mapObjectTypes: 'settings/getMapObjectTypes',
      networkId: 'settings/getNetworkId',
      network: 'settings/getNetworkModel',
      roles: 'getRoles',
      selected: 'map/getSelected',
      mapObject: 'selected/getMapObject',
      filters: 'settings/getFilter',
      settingsLoadedAt: 'settings/getSettingsLoadedAt',
      mapObjectsLoadedAt: 'settings/getMapObjectsLoadedAt',
    }),
    typesOnNetwork() {
      if (this.mapObjectTypes === null || this.mapObjectTypes === undefined) {
        return [];
      }
      if (Object.values(this.mapObjectTypes).length === 0) {
        return [];
      }
      return Object.values(this.mapObjectTypes)
        .filter(item => item.on_network)
        .sort(this.sortTypeByGeoType);
    },
    openActionStatuses() {
      if (this.actionStatuses === null || this.actionStatuses === undefined) {
        return [];
      }
      return Object.values(this.actionStatuses)
        .filter(status => status.actionRoleStatus.data.is_open === true);
    },
  },
  data() {
    return {
      firstInit: true,
      oms: null,
      currentZoom: null,
      pointsLayer: null,
      linesBackgroundLayer: null,
      selectedLinesLayer: null,
      polygonsLayer: null,
      polygonPointsLayer: null,
      actionsLayer: null,
      clusterObjectsLayer: null,
      objectActionsLayer: null,
      lineActionsLayer: null,
      childrenLayer: null,
      pointsLayerMap: {},
      selectedLinesLayerMap: {},
      polygonsLayerMap: {},
      actionsLayerMap: {},
      childrenLayerMap: {},
      showActions: true,
      showActionsOnMap: true,
      showChildrenOnMap: true,
      clustering: true,
      chunkSize: 1000,
      bboxActiveZoomLevel: 12,
      hideActionsZoomLevel: 9,
      DISABLE_CLUSTERING_AT_ZOOM: 22,
      LINE_WEIGHT: 5,
      LINE_OPACITY: 0.55,
      POLYGON_WEIGHT: 4,
      POLYGON_OPACITY: 0.55,
      DEFAULT_MARKER_COLOR: 'blue',
      SELECTED_MARKER_COLOR: 'red',
      DEFAULT_POLYLINE_COLOR: 'grey',
      SELECTED_POLYLINE_COLOR: 'blue',
      DEFAULT_POLYGON_COLOR: 'blue',
      SELECTED_POLYGON_COLOR: 'red',
      DEFAULT_DELETED_COLOR: 'grey',
      SELECTED_DELETED_COLOR: 'grey',
      OBSTRUCTED_COLOR: 'red',
      mapInitialized: false,
      allObjectsRelatedToSelected: [],
      connectedLines: [],
      GeoType,
      MapMode,
      minZoom: 8,
    };
  },
  methods: {
    ...mapActions({
      fetchMapObject: 'selected/fetchMapObject',
      findMapObjectByLatLng: 'selected/findMapObjectByLatLng',
      getChildrenForMapObject: 'settings/getChildrenForMapObject',
      getActionsForMapObjectIds: 'settings/getActionsForMapObjectIds',
      updateMapObject: 'settings/updateMapObject',
    }),
    sortTypeByGeoType(a, b) {
      if (a.geo_type === 'point' && b.geo_type === 'linestring') {
        return 1;
      }
      if (a.geo_type === 'linestring' && b.geo_type === 'point') {
        return -1;
      }
      return 0;
    },
    actionsOnMap() {
      const actionsData = Object.values(dataCache.getActions());

      if (actionsData === null || actionsData === undefined) {
        return [];
      }
      const statuses = this.openActionStatuses.map(item => item.id);
      let actions = actionsData
        .filter(action => action.map_object_id === null
          && action.geo_object_id !== null
          && statuses.includes(action.action_status_id));

      if (this.filters) {
        if (this.filters.actionPriority && this.filters.actionPriority.length > 0) {
          actions = actions.filter(item => this.filters.actionPriority.includes(item.action_priority_id));
        }
        if (this.filters.actionStatus && this.filters.actionStatus.length > 0) {
          actions = actions.filter(item => this.filters.actionStatus.includes(item.action_status_id));
        }
        if (this.filters.dynamicActionType && this.filters.dynamicActionType.length > 0) {
          actions = actions.filter(item => this.filters.dynamicActionType.includes(item.dynamic_action_type_id));
        }
      }
      return actions;
    },
    initMap() {
      console.log('ObjectsLayer.initMap()');
      this.mapInitialized = false;
      const startTime = performance.now();
      this.showActions = this.$route.name.includes('route_management');
      EventBus.$emit('Map.loading', true);
      if (this.firstInit) {
        // Here the events for zooming and dragging
        this.map.on('zoomend', (event) => {
          const zoom = event.target.getZoom();

          if (zoom > this.hideActionsZoomLevel) {
            if (!this.showActionsOnMap && this.showActions) {
              this.map.addLayer(this.actionsLayer);
              this.map.addLayer(this.objectActionsLayer);
              this.showActionsOnMap = true;
            }
          } else if (this.showActionsOnMap) {
            this.map.removeLayer(this.actionsLayer);
            this.map.removeLayer(this.objectActionsLayer);
            this.showActionsOnMap = false;
          }
          this.currentZoom = event.target.getZoom();
        });
        this.map.on('moveend', this.onMoveEnd);
        this.map.on('areaselected', (e) => {
          console.log('areaselected', e, e.bounds);
          this.selectAreaOfObjects(e.bounds);
        });
        this.map.on('selectarea:selected', (e) => {
          console.log('selectarea:selected', e, e.bounds);
          this.selectAreaOfObjects(e.bounds);
        });
        this.firstInit = false;
      }
      console.log('ObjectsLayer.initMap() 2');
      this.currentZoom = this.map.getZoom();

      this.resetMap();

      console.log('ObjectsLayer.initMap() 3');
      this.drawObjectsOnMap();
      const endTime = performance.now();
      console.log(`Map Init took ${endTime - startTime} milliseconds`);

      this.mapInitialized = true;
      EventBus.$emit('Map.loading', false);
    },
    onMoveEnd() {
      if (!this.clustering) {
        EventBus.$emit('Map.redraw');
      }
    },
    async drawObjectsOnMap() {
      const mapObjectsData = Object.values(await dataCache.getMapObjects());
      const groups = this.groupBy(mapObjectsData, 'map_object_type_id');
      const types = this.clustering ? this.typesOnNetwork : Object.values(this.mapObjectTypes);
      const geoTypeHandlers = {
        point: this.createPoint,
        polygon: this.createPolygon,
      };

      types.forEach((type) => {
        const dataByType = groups.get(type.id);
        if (!dataByType || dataByType.length === 0) return;

        const filteredData = this.filterObjectsOnMap(dataByType);

        if (type.geo_type === 'linestring') {
          this.drawLineClustersOnMap(filteredData, type.id);
          return;
        }
        const handler = geoTypeHandlers[type.geo_type];
        if (!handler) return;

        /**
         * Load data on map in chunks, to let GC do his this in the meantime and update map
         */
        for (let i = 0; i < filteredData.length; i += this.chunkSize) {
          setTimeout(() => {
            filteredData.slice(i, i + this.chunkSize).forEach((data) => {
              handler.call(this, data); // Ensure correct `this` context
            });
          }, i / 10);
        }
      });

      if (this.showActions) {
        this.createActionPoints();
      }
    },
    redrawLinesOnMap() {
      const startTime = performance.now();
      this.linesBackgroundLayer.clearLayers();
      const mapObjectsData = Object.values(dataCache.getMapObjects());
      const groups = this.groupBy(mapObjectsData, 'map_object_type_id');
      const types = this.clustering ? this.typesOnNetwork : Object.values(this.mapObjectTypes);

      types.forEach((type) => {
        const dataByType = groups.get(type.id);
        if (!dataByType || dataByType.length === 0) return;

        const filteredData = this.filterObjectsOnMap(dataByType);

        if (type.geo_type === 'linestring') {
          this.drawLineClustersOnMap(filteredData, type.id);
        }
      });
      const endTime = performance.now();
      console.log(`redrawLinesOnMap took ${endTime - startTime} milliseconds`);
    },
    async drawLineClustersOnMap(linesByTypeFiltered, mapObjectTypeId) {
      const startTime = performance.now();

      let lines = linesByTypeFiltered;

      if (this.connectedLines.length > 0) {
        const connectedLineIds = this.connectedLines.map(item => item.id);
        lines = lines.filter(item => !connectedLineIds.includes(item.id));
      }

      lines.forEach((mapObject) => {
        this.createLineAction(mapObject);
      });

      const linesNormal = lines.filter(item => item.is_obstructed === false);
      const linesObstructed = lines.filter(item => item.is_obstructed === true);

      this.createLineCluster(mapObjectTypeId, linesObstructed, true);
      this.createLineCluster(mapObjectTypeId, linesNormal, false);

      const linesSelected = linesByTypeFiltered.filter(
        x => this.selected.filter(y => y.id === x.id).length > 0,
      );
      linesSelected.forEach((line) => {
        this.createLine(line);
      });
      const endTime = performance.now();
      console.log(`drawLineClustersOnMap took ${endTime - startTime} milliseconds`);
    },
    createLineCluster(mapObjectTypeId, mapObjects, isObstructed = false) {
      if (mapObjects.length > 0) {
        const mergedCoords = mapObjects.map(item => item.geoObject.data.geometry.coordinates.map(([lng, lat]) => {
          if (Number.isNaN(parseFloat(lat)) || Number.isNaN(parseFloat(lng))) {
            return [0, 0];
          }
          return [lat, lng];
        }));
        const iconData = mapObjectTypeId ? this.getIconDataForType(mapObjectTypeId) : null;
        let color = (iconData && iconData.color) ? iconData.color : this.DEFAULT_POLYLINE_COLOR;
        if (isObstructed) {
          color = this.OBSTRUCTED_COLOR;
        }
        const line = new L.Polyline(mergedCoords, {
          color,
          weight: this.LINE_WEIGHT,
          opacity: this.LINE_OPACITY,
        });
        line.layer_type = GeoType.linestring;
        line.map_object_type_id = mapObjectTypeId;
        line.on('click', (e) => {
          this.onLineClusterClick(e, line);
        });
        this.linesBackgroundLayer.addLayer(line);
      }
    },
    filterObjectsOnMap(mapObjects) {
      const startTime = performance.now();
      if (!this.filters || Object.keys(this.filters).length === 0) {
        const endTime = performance.now();
        console.log(`filterObjectsOnMap took ${endTime - startTime} milliseconds`);
        return mapObjects;
      }

      let objects = JSON.parse(JSON.stringify(mapObjects));

      let actionFilter = false;
      if (this.filters.actionPriority && this.filters.actionPriority.length > 0) {
        actionFilter = true;
      }
      if (this.filters.actionStatus && this.filters.actionStatus.length > 0) {
        actionFilter = true;
      }
      if (this.filters.dynamicActionType && this.filters.dynamicActionType.length > 0) {
        actionFilter = true;
      }

      if (this.filters.obstructed) {
        objects = objects.filter(
          item => item.is_obstructed,
        );
      }
      if (this.filters.deleted) {
        objects = objects.filter(
          item => item.deleted_at !== null,
        );
      } else {
        objects = objects.filter(
          item => !Object.hasOwn(item, 'deleted_at') || item.deleted_at === null,
        );
      }

      if (this.filters.mapObjectType && this.filters.mapObjectType.length > 0) {
        const selectedTypes = this.filters.mapObjectType.map(id => parseInt(id, 10));
        if (this.clustering) {
          objects = objects.filter(item => selectedTypes.includes(item.map_object_type_id)
            || dataCache.filterChildrenForMapObjectId(item.id, 'map_object_type_id', selectedTypes));
        } else {
          objects = objects.filter(
            item => this.filters.mapObjectType.includes(item.map_object_type_id)
              || this.filters.mapObjectType.includes(parseInt(item.map_object_type_id, 10)),
          );
        }
      }
      if (this.filters.mapObjectStatus && this.filters.mapObjectStatus.length > 0) {
        const selectedStatuses = this.filters.mapObjectStatus.map(id => parseInt(id, 10));
        objects = objects.filter(item => selectedStatuses.includes(item.map_object_status_id)
          || dataCache.filterChildrenForMapObjectId(item.id, 'map_object_status_id', selectedStatuses));
      }
      if (this.filters.mapObjectSelection && this.filters.mapObjectSelection.length > 0) {
        const selectionsInFilter = Object.values(dataCache.getSelections())
          .filter(item => this.filters.mapObjectSelection.includes(item.id));
        const mapObjectsInSelection = selectionsInFilter
          .map(item => item.mapObjectId.data.map(innerItem => innerItem.id))
          .flat();
        objects = objects.filter(item => mapObjectsInSelection.includes(item.id)
          || dataCache.filterChildrenForMapObjectId(item.id, 'id', mapObjectsInSelection));
      }

      if (!this.clustering) {
        const mapBounds = this.map.getBounds();
        mapBounds.pad(0.2);
        objects = objects.filter(item => L.geoJSON(item.geoObject.data).getBounds().intersects(mapBounds));
      }

      if (actionFilter === true) {
        objects = objects.filter((item) => {
          const [actionCount, childrenActionCount] = dataCache.countOpenActionsForMapObjectId(item.id, this.filters);
          return actionCount > 0 || childrenActionCount > 0;
        });
      }
      const endTime = performance.now();
      console.log(`filterObjectsOnMap took ${endTime - startTime} milliseconds`);
      return objects;
    },
    maxClusterRadius(zoomLevel) {
      if (zoomLevel < 15) {
        return 60;
      }
      return 1;
    },
    maxClusterRadiusForMultiSelect(zoomLevel) {
      if (zoomLevel < 15) {
        return 0.000001;
      }
      return 1;
    },
    groupBy(list, key) {
      const groupedMap = new Map();
      list.forEach((e) => {
        let thisList = groupedMap.get(e[key]);
        if (thisList === undefined) {
          thisList = [];
          groupedMap.set(e[key], thisList);
        }
        thisList.push(e);
      });
      return groupedMap;
    },
    createPoint(mapObject) {
      const coords = this.getCoordinates(mapObject, 'point');
      let point = new L.Marker(coords);
      point.id = mapObject.id;
      point.map_object_id = mapObject.id;
      point.layer_type = GeoType.point;
      point = this.updatePoint(point, mapObject);
      this.pointsLayer.addLayer(point);
      // eslint-disable-next-line no-underscore-dangle
      this.$set(this.pointsLayerMap, mapObject.id, point._leaflet_id);
      return point;
    },
    updatePoint(data, mapObject = null, customCoords = null) {
      const point = data;
      const label = mapObject ? this.getLabelForMapObject(mapObject) : data.label;

      if (customCoords) {
        point.setLatLng(customCoords);
      } else if (mapObject) {
        point.setLatLng(this.getCoordinates(mapObject, 'point'));
      }
      let actionCountToShow = 0;
      let actionCount = point.action_count;
      let childrenActionCount = point.children_action_count;
      if (mapObject) {
        point.parent_id = mapObject.parent_id;
        point.map_object_type_id = mapObject.map_object_type_id;
        point.name = mapObject.name;
        point.selected = (this.selected.filter(y => y.id === mapObject.id).length > 0);
        point.is_obstructed = mapObject.is_obstructed;
        point.off('click');
        point.on('click', (e) => {
          this.onPointClick(e, mapObject);
        });
      }
      if (this.showActions) {
        [actionCount, childrenActionCount] = dataCache.countOpenActionsForMapObjectId(point.id, this.filters);
        actionCountToShow = actionCount + childrenActionCount;

        if (this.mapObject
          && this.allObjectsRelatedToSelected.includes(this.mapObject.id)
          && this.allObjectsRelatedToSelected.includes(point.map_object_id)) {
          actionCountToShow = actionCount;
        }
      }
      point.label = label || '';
      point.action_count = actionCount;
      point.children_action_count = childrenActionCount;
      if (parseInt(this.$route.params.id, 10) === point.map_object_id
        && this.mapObject
        && this.mapObject.id === point.map_object_id
      ) {
        point.selected = true;
      }
      const iconData = this.getIconDataForType(point.map_object_type_id);
      const icon = this.getIconByMapObjectType(iconData, actionCountToShow, label || '', point.selected, false, point.is_obstructed);
      point.setIcon(icon);
      return point;
    },
    createLine(mapObject) {
      const coords = this.getCoordinates(mapObject, 'linestring');
      let line = new L.Polyline(coords, {
        color: this.DEFAULT_POLYLINE_COLOR,
        weight: this.LINE_WEIGHT,
        opacity: this.LINE_OPACITY,
      });
      line.id = mapObject.id;
      line.layer_type = GeoType.linestring;
      line.map_object_id = mapObject.id;
      line = this.updateLine(line, mapObject);
      this.selectedLinesLayer.addLayer(line);
      // eslint-disable-next-line no-underscore-dangle
      this.selectedLinesLayerMap[mapObject.id] = line._leaflet_id;
      return line;
    },
    updateLine(data, mapObject = null) {
      const line = data;
      let actionCountToShow = 0;
      let actionCount = line.action_count;
      let childrenActionCount = line.children_action_count;
      if (mapObject) {
        line.parent_id = mapObject.parent_id;
        line.map_object_type_id = mapObject.map_object_type_id;
        line.is_obstructed = mapObject.is_obstructed;
        line.selected = (this.selected.filter(y => y.id === mapObject.id).length > 0);
        line.start_point = this.getStartPointIdForLine(mapObject);
        line.end_point = this.getEndPointIdForLine(mapObject);
        line.setLatLngs(this.getCoordinates(mapObject, 'linestring'));
        line.off('click');
        line.on('click', (e) => {
          this.onLineClick(e, mapObject);
        });
      }
      const iconData = line.map_object_type_id ? this.getIconDataForType(line.map_object_type_id) : null;
      let color = (iconData && iconData.color) ? iconData.color : this.DEFAULT_POLYLINE_COLOR;
      if (line.selected) {
        color = (iconData && iconData.color_selected)
          ? iconData.color_selected
          : this.this.SELECTED_POLYLINE_COLOR;
      } else if (line.is_obstructed) {
        color = this.OBSTRUCTED_COLOR;
      }
      if (this.showActions) {
        [actionCount, childrenActionCount] = dataCache.countOpenActionsForMapObjectId(line.id, this.filters);
        actionCountToShow = actionCount + childrenActionCount;
        if (this.mapObject && this.allObjectsRelatedToSelected
          .includes(this.mapObject.id)) {
          actionCountToShow = actionCount;
        }
      }
      line.setStyle({
        color,
        weight: this.LINE_WEIGHT,
        opacity: this.LINE_OPACITY,
      });
      line.action_count = actionCount;
      line.children_action_count = childrenActionCount;
      line.redraw();

      if (actionCountToShow > 0) {
        const centerPoint = this.getCenterPointOfLinestring(line);
        let marker = null;
        this.lineActionsLayer.eachLayer((layer) => {
          if (layer.map_object_id === line.map_object_id) {
            marker = layer;
          }
        });
        if (!marker) {
          marker = new L.Marker(centerPoint);
          marker.id = line.map_object_id;
          marker.map_object_id = line.map_object_id;
          const icon = this.getActionIcon(actionCountToShow);
          marker.setIcon(icon);
          this.lineActionsLayer.addLayer(marker);
        } else {
          const icon = this.getActionIcon(actionCountToShow);
          marker.setIcon(icon);
        }
        if (mapObject) {
          marker.off('click');
          marker.on('click', (e) => {
            this.onLineClick(e, mapObject);
          });
        }
      }
      return line;
    },
    createLineAction(mapObject) {
      let actionCountToShow = 0;
      let actionCount = 0;
      let childrenActionCount = 0;
      if (this.showActions) {
        [actionCount, childrenActionCount] = dataCache.countOpenActionsForMapObjectId(mapObject.id, this.filters);
        actionCountToShow = actionCount + childrenActionCount;
        if (this.mapObject && this.allObjectsRelatedToSelected
          .includes(this.mapObject.id)) {
          actionCountToShow = actionCount;
        }
      }

      if (actionCountToShow > 0) {
        const coords = this.getCoordinates(mapObject, 'linestring');
        const line = new L.Polyline(coords);
        const centerPoint = this.getCenterPointOfLinestring(line);
        let marker = null;
        this.lineActionsLayer.eachLayer((layer) => {
          if (layer.map_object_id === mapObject.id) {
            marker = layer;
          }
        });
        if (!marker) {
          marker = new L.Marker(centerPoint);
          marker.id = mapObject.id;
          marker.map_object_id = line.map_object_id;
          const icon = this.getActionIcon(actionCountToShow);
          marker.setIcon(icon);
          this.lineActionsLayer.addLayer(marker);
        } else {
          const icon = this.getActionIcon(actionCountToShow);
          marker.setIcon(icon);
        }
        if (mapObject) {
          marker.off('click');
          marker.on('click', (e) => {
            this.onLineClick(e, mapObject);
          });
        }
      }
    },
    createPolygon(mapObject) {
      const coords = this.getCoordinates(mapObject, 'polygon');
      let polygon = new L.Polygon(coords, {
        color: this.DEFAULT_POLYGON_COLOR,
        weight: this.POLYGON_WEIGHT,
        opacity: this.POLYGON_OPACITY,
      });
      polygon.id = mapObject.id;
      polygon.layer_type = GeoType.polygon;
      polygon.map_object_id = mapObject.id;
      const point = this.createPointForPolygon(polygon, mapObject);
      this.polygonPointsLayer.addLayer(point);
      polygon = this.updatePolygon(polygon, mapObject);
      this.polygonsLayer.addLayer(polygon);
      // eslint-disable-next-line no-underscore-dangle
      this.polygonsLayerMap[mapObject.id] = polygon._leaflet_id;
      return polygon;
    },
    updatePolygon(data, mapObject = null) {
      const polygon = data;

      if (mapObject) {
        polygon.parent_id = mapObject.parent_id;
        polygon.map_object_type_id = mapObject.map_object_type_id;
        polygon.label = mapObject.label || '';
        polygon.selected = (this.selected.filter(y => y.id === mapObject.id).length > 0);
        polygon.is_obstructed = mapObject.is_obstructed;
        polygon.setLatLngs(this.getCoordinates(mapObject, 'polygon'));
        polygon.off('click');
        polygon.on('click', (e) => {
          this.onPolygonClick(e, mapObject);
        });
      }
      const iconData = this.getIconDataForType(polygon.map_object_type_id);
      let color = (iconData && iconData.color) ? iconData.color : this.DEFAULT_POLYGON_COLOR;
      if (polygon.selected) {
        color = (iconData && iconData.color_selected) ? iconData.color_selected
          : this.SELECTED_POLYGON_COLOR;
      } else if (polygon.is_obstructed) {
        color = this.OBSTRUCTED_COLOR;
      }
      polygon.setStyle({
        color,
        weight: this.POLYGON_WEIGHT,
        opacity: this.POLYGON_OPACITY,
      });
      polygon.redraw();
      let point = null;
      this.polygonPointsLayer.eachLayer((layer) => {
        if (layer.map_object_id === polygon.map_object_id) {
          point = layer;
        }
      });
      if (point) {
        this.updatePointForPolygon(point, polygon, mapObject);
      }
      return polygon;
    },
    createPointForPolygon(polygon, mapObject) {
      let longitude;
      let latitude;

      const coords = this.getCoordinates(mapObject, 'point');
      if (coords !== null) {
        [longitude, latitude] = coords;
      } else {
        // Point on the polygon
        longitude = polygon.getBounds().getCenter().lng;
        latitude = polygon.getBounds().getCenter().lat;
      }
      let point = new L.Marker([latitude, longitude]);
      point.id = mapObject.id;
      point.map_object_id = mapObject.id;
      point = this.updatePoint(point, mapObject, [latitude, longitude]);

      return point;
    },
    updatePointForPolygon(data, polygon, mapObject = null) {
      let longitude;
      let latitude;

      let newCoordinates = null;
      if (mapObject) {
        const coords = this.getCoordinates(mapObject, 'point');
        if (coords !== null) {
          [longitude, latitude] = coords;
        } else {
          // Point on the polygon
          longitude = polygon.getBounds().getCenter().lng;
          latitude = polygon.getBounds().getCenter().lat;
        }
        newCoordinates = [latitude, longitude];
      }
      let point = data;
      point = this.updatePoint(point, mapObject, newCoordinates);
      return point;
    },
    createActionPoints() {
      this.actionsLayer.clearLayers();
      this.actionsOnMap().forEach((data) => {
        const marker = this.createAction(data);
        marker.addTo(this.actionsLayer);
        // eslint-disable-next-line no-underscore-dangle
        this.actionsLayerMap[data.id] = marker._leaflet_id;
      });
    },
    drawObjectActionsOnMap(mapObject) {
      this.objectActionsLayer.clearLayers();
      this.getActionsForMapObjectIds({ ids: [mapObject.id] }).then((actions) => {
        actions.forEach((action) => {
          const type = this.dynamicActionTypes[action.dynamic_action_type_id];
          const status = this.openActionStatuses.filter(item => item.id === action.action_status_id);
          if (type !== null && type !== undefined && type.own_geo && type.own_geo === true
          && status !== null && status !== undefined && status.length > 0) {
            const marker = this.createAction(action);
            if (marker) {
              marker.addTo(this.objectActionsLayer);
              const actionCoords = this.getCoordinates(action, 'point');
              const objectCoords = this.getCoordinates(mapObject, 'point');
              if (objectCoords !== null) {
                const polyline = this.createConnectingLine(actionCoords, objectCoords);
                polyline.addTo(this.objectActionsLayer);
              }
            }
          }
        });
      });
    },
    createAction(action) {
      const coords = this.getCoordinates(action);
      if (!coords) { return null; }
      const marker = new L.Marker(coords);
      const label = action.id;
      marker.id = action.id;
      marker.action_id = action.id;
      marker.label = label;
      const icon = this.getIconByMapObjectType({
        type: 'action',
        color: 'orange',
        color_selected: 'red',
      }, 0, label, false);
      marker.setIcon(icon);
      marker.on('click', (e) => {
        this.onActionClick(e, action.id);
      });
      return marker;
    },
    createConnectingLine(coords1, coords2) {
      return new L.Polyline([coords1, coords2], {
        weight: 1,
        color: 'black',
      });
    },
    removeMapObjectFromMap(mapObjectId) {
      // console.log('removeMapObjectFromMap', mapObjectId);

      const point = this.pointsLayer.getLayer(this.pointsLayerMap[mapObjectId]);
      if (point) {
        this.pointsLayer.removeLayer(point);
        point.off();
        this.$delete(this.pointsLayerMap, mapObjectId);
      }
      const line = this.selectedLinesLayer.getLayer(this.selectedLinesLayerMap[mapObjectId]);
      const selected = (this.selected.filter(y => y.id === mapObjectId).length > 0);
      if (line && !selected) {
        this.selectedLinesLayer.removeLayer(line);
        line.off();
        this.$delete(this.selectedLinesLayerMap, mapObjectId);
      }
      const polygon = this.polygonsLayer.getLayer(this.polygonsLayerMap[mapObjectId]);
      if (polygon) {
        this.polygonsLayer.removeLayer(polygon);
        polygon.off();
        this.$delete(this.polygonsLayerMap, mapObjectId);
      }
      this.polygonPointsLayer.eachLayer((layer) => {
        if (layer.map_object_id === mapObjectId) {
          this.polygonPointsLayer.removeLayer(layer);
          layer.off();
        }
      });
      this.childrenLayer.eachLayer((layer) => {
        if (layer.map_object_id === mapObjectId) {
          this.childrenLayer.removeLayer(layer);
          layer.off();
        }
      });
      this.redrawLinesOnMap();
    },
    removeActionFromMap(actionId) {
      this.actionsLayer.eachLayer((layer) => {
        if (layer.id === actionId) {
          this.actionsLayer.removeLayer(layer);
          layer.off();
        }
      });
    },
    redrawAllActionMapObjectsOnMap() {
      console.log('ObjectsLayer.redrawAllActionMapObjectsOnMap');
      const startTime = performance.now();
      const mapObjectIds = dataCache.getAllMapObjectIdsThatHaveActions();
      mapObjectIds.forEach((id) => {
        const mapObjectId = parseInt(id, 10);
        if (this.pointsLayerMap[mapObjectId]) {
          const layer = this.pointsLayer.getLayer(this.pointsLayerMap[mapObjectId]);
          if (!layer) {
            return;
          }
          const mapObject = dataCache.getMapObjectById(layer.map_object_id);
          if (mapObject) {
            this.updatePoint(layer, mapObject);
          }
        }
        if (this.polygonsLayerMap[mapObjectId]) {
          const layer = this.polygonsLayer.getLayer(this.polygonsLayerMap[mapObjectId]);
          if (!layer) {
            return;
          }
          const mapObject = dataCache.getMapObjectById(layer.map_object_id);
          if (mapObject) {
            this.updatePolygon(layer, mapObject);
          }
        }
      });
      this.redrawLinesOnMap();
      this.selectedLinesLayer.eachLayer(async (layer) => {
        const mapObject = dataCache.getMapObjectById(layer.map_object_id);
        if (mapObject) {
          this.updateLine(layer, mapObject);
        }
      });
      this.childrenLayer.eachLayer(async (layer) => {
        const mapObject = dataCache.getMapObjectById(layer.map_object_id);
        if (mapObject) {
          const child = layer;
          child.label = mapObject.label || mapObject.name;
          this.updateChildOnMap(child);
        }
      });
      if (this.showActions) {
        this.createActionPoints();
      }
      const endTime = performance.now();
      console.log(`ObjectsLayer.redrawAllActionMapObjectsOnMap took ${endTime - startTime} milliseconds`);
    },
    redrawMapObjectOnMap(mapObject, renewActions = false) {
      console.log('ObjectsLayer redrawMapObjectOnMap', mapObject);
      const type = this.mapObjectTypes[mapObject.map_object_type_id];
      if (!type) {
        return;
      }
      if (type.on_network) {
        if (type.geo_type === GeoType.point) {
          const point = this.pointsLayer.getLayer(this.pointsLayerMap[mapObject.id]);
          if (point) {
            this.updatePoint(point, mapObject);
          } else {
            this.createPoint(mapObject);
          }
        } else if (type.geo_type === GeoType.linestring) {
          const line = this.selectedLinesLayer.getLayer(this.selectedLinesLayerMap[mapObject.id]);
          const selected = (this.selected.filter(y => y.id === mapObject.id).length > 0);
          if (line && selected) {
            this.updateLine(line, mapObject);
          } else if (line) {
            this.selectedLinesLayer.removeLayer(line);
            line.off();
            delete this.selectedLinesLayerMap[mapObject.id];
          } else if (selected) {
            this.createLine(mapObject);
          }
        } else if (type.geo_type === GeoType.polygon) {
          const polygon = this.polygonsLayer.getLayer(this.polygonsLayerMap[mapObject.id]);
          if (polygon) {
            this.updatePolygon(polygon, mapObject);
          } else {
            this.createPolygon(mapObject);
          }
        }
      } else {
        const coords = this.getCoordinates(mapObject, 'point');
        let itemOnMap = false;
        this.childrenLayer.eachLayer((layer) => {
          if (layer.map_object_id === mapObject.id) {
            itemOnMap = true;
            const child = layer;
            child.label = mapObject.label || mapObject.name;
            child.setLatLng(coords);
            this.updateChildOnMap(child, renewActions);
            const parent = dataCache.getMapObjectById(mapObject.parent_id);
            console.log('parent', parent);
            if (parent) {
              this.redrawMapObjectOnMap(parent);
            }
          }
          if (layer.line_map_object_id === mapObject.id) {
            layer.setLatLngs([coords, layer.parent_coords]);
          }
        });
        if (mapObject.parent_id && !itemOnMap) {
          this.addChildToMap(mapObject);
        }
      }
      if (this.showActions) {
        this.drawObjectActionsOnMap(mapObject);
      }
    },
    async updateChildOnMap(data) {
      const point = data;
      let actionCountToShow = 0;

      if (this.showActions) {
        actionCountToShow = point.action_count + point.children_action_count;
        const mapObject = dataCache.getMapObjectById(point.map_object_id);
        let actionCount = point.action_count;
        let childrenActionCount = point.children_action_count;
        if (mapObject) {
          [actionCount, childrenActionCount] = dataCache.countOpenActionsForMapObjectId(mapObject.id, this.filters);
          point.action_count = actionCount;
          point.children_action_count = childrenActionCount;
        }
        if (this.mapObject && this.allObjectsRelatedToSelected
          .includes(this.mapObject.id)) {
          actionCountToShow = point.action_count;
        }
      }

      const iconData = this.getIconDataForType(point.map_object_type_id);
      const icon = this.getIconByMapObjectType(iconData, actionCountToShow, point.label || '', point.selected);
      point.setIcon(icon);
    },
    selectChildPointOnMap(layer) {
      const point = layer;
      point.selected = true;
      this.updateChildOnMap(point);
    },
    unselectChildPointOnMap(layer) {
      const point = layer;
      point.selected = false;
      this.updateChildOnMap(point);
    },
    setSelectedOnMap() {
      console.log('setSelectedOnMap', this.selected);
      const layersToCheck = [
        this.pointsLayer,
        this.polygonsLayer,
      ];
      layersToCheck.forEach((layerGroup) => {
        layerGroup.eachLayer((data) => {
          const layer = data;
          if (layer.selected === true
            && this.selected.filter(item => item.id === layer.map_object_id).length === 0
          ) {
            layer.selected = false;
          } else if (layer.selected === false
            && this.selected.filter(item => item.id === layer.map_object_id).length > 0
          ) {
            layer.selected = true;
          }
          switch (layer.layer_type) {
            case GeoType.point:
              this.updatePoint(layer);
              break;
            case GeoType.polygon:
              this.updatePolygon(layer);
              break;
            default:
              break;
          }
        });
      });

      this.selectedLinesLayer.eachLayer((data) => {
        const layer = data;
        if (this.selected.filter(item => item.id === layer.map_object_id).length === 0) {
          this.selectedLinesLayer.removeLayer(layer);
          layer.off();
          delete this.selectedLinesLayerMap[layer.map_object_id];
        }
      });
      this.selected.filter(item => (
        item.geo_type === 'linestring'
        || this.geoTypeOfGeoObject(item.geoObject) === 'LineString'
      )).forEach((object) => {
        const line = this.selectedLinesLayer.getLayer(this.selectedLinesLayerMap[object.id]);
        if (!line) {
          this.createLine(object);
        }
      });
      this.childrenLayer.eachLayer((layer) => {
        if (layer.selected === true
          && this.selected.filter(item => item.id === layer.map_object_id).length === 0
        ) {
          this.unselectChildPointOnMap(layer);
        } else if (layer.selected === false
          && this.selected.filter(item => item.id === layer.map_object_id).length > 0
        ) {
          this.selectChildPointOnMap(layer);
        }
      });
    },
    onPointClick(event, mapObject) {
      console.log('onPointClick', event, mapObject, this.map.mapMode, this.map.drawType);
      if ((this.map.mapMode === MapMode.objectCreate || this.map.mapMode === MapMode.objectUpdate)
        && this.map.drawType !== GeoType.linestring) {
        return;
      }
      EventBus.$emit('Map.clickMapObject', mapObject, GeoType.point);
      L.DomEvent.stopPropagation(event);
    },
    async onLineClick(event, mapObject) {
      console.log('onLineClick', event, mapObject, this.map.mapMode, this.map.drawType);
      if (this.map.mapMode === MapMode.objectCreate || this.map.mapMode === MapMode.objectUpdate) {
        return;
      }
      EventBus.$emit('Map.clickMapObject', mapObject, GeoType.linestring);
      L.DomEvent.stopPropagation(event);
    },
    async onLineClusterClick(event, line) {
      console.log('onLineClusterClick', event, line, this.map.mapMode, this.map.drawType);
      if (this.map.mapMode === MapMode.objectCreate || this.map.mapMode === MapMode.objectUpdate) {
        return;
      }

      const startTime = performance.now();
      const mapObjectTypeId = line.map_object_type_id;
      const mapObjectsData = Object.values(await dataCache.getMapObjects())
        .filter(item => item.map_object_type_id === mapObjectTypeId);

      const p = this.map.latLngToLayerPoint(event.latlng);

      let best = {
        line: null,
        distPx: Infinity,
        segment: null,
        snappedLatLng: null,
      };

      const project = ll => this.map.latLngToLayerPoint(ll);

      // eslint-disable-next-line no-restricted-syntax
      for (const object of mapObjectsData) {
        const latlngs = object.geoObject.data.geometry.coordinates.map(([lng, lat]) => {
          if (Number.isNaN(parseFloat(lat)) || Number.isNaN(parseFloat(lng))) {
            return [0, 0];
          }
          return [lat, lng];
        });

        for (let i = 0; i < latlngs.length - 1; i += 1) {
          const aLL = latlngs[i];
          const bLL = latlngs[i + 1];
          const a = project(aLL);
          const b = project(bLL);

          // pixel distance from point to the segment
          const d = L.LineUtil.pointToSegmentDistance(p, a, b);

          if (d < best.distPx) {
            // also compute the projected closest point to return a snapped LatLng
            const closest = L.LineUtil.closestPointOnSegment(p, a, b);
            best = {
              line: object,
              distPx: d,
              segment: [aLL, bLL],
              snappedLatLng: this.map.layerPointToLatLng(closest),
            };
          }
        }
      }

      console.log('p', p);
      console.log('best', best);
      const endTime = performance.now();
      console.log(`Finding the correct line took ${endTime - startTime} milliseconds`);

      if (best.distPx < 6) {
        EventBus.$emit('Map.clickMapObject', best.line, GeoType.linestring);
      }
      L.DomEvent.stopPropagation(event);
    },
    async findLinesInBounds(bounds) {
      const startTime = performance.now();
      const mapObjectsData = Object.values(await dataCache.getMapObjects());
      const mapObjectLines = mapObjectsData.filter(item => item.geoObject.data.geometry.type === 'LineString');
      const filteredData = this.filterObjectsOnMap(mapObjectLines);

      // eslint-disable-next-line no-restricted-syntax
      for (const object of filteredData) {
        const latlngs = object.geoObject.data.geometry.coordinates.map(([lng, lat]) => {
          if (Number.isNaN(parseFloat(lat)) || Number.isNaN(parseFloat(lng))) {
            return [0, 0];
          }
          return [lat, lng];
        });
        const line = new L.Polyline(latlngs);
        if (line.getBounds().intersects(bounds)) {
          EventBus.$emit('Map.clickMapObject', object, GeoType.linestring);
        }
      }
      const endTime = performance.now();
      console.log(`findLinesInBounds took ${endTime - startTime} milliseconds`);
    },
    onPolygonClick(event, mapObject) {
      console.log('onPolygonClick', event, mapObject, this.map.mapMode);
      if (this.map.mapMode === MapMode.objectCreate || this.map.mapMode === MapMode.objectUpdate) {
        return;
      }
      EventBus.$emit('Map.clickMapObject', mapObject, GeoType.polygon);
      L.DomEvent.stopPropagation(event);
    },
    onActionClick(event, actionId) {
      console.log('onActionClick', event, actionId);
      EventBus.$emit('Map.clickAction', actionId);
    },
    async addChildToMap(data, addConnectingLine = true) {
      const coords = this.getCoordinates(data, 'point');
      const marker = new L.Marker(coords);
      const mapObjectId = data.id;
      const mapObjectTypeId = data.map_object_type_id;
      const [actionCount, childrenActionCount] = dataCache.countOpenActionsForMapObjectId(data.id, this.filters);
      const label = data.name;
      marker.parent_id = data.parent_id;
      marker.map_object_id = mapObjectId;
      marker.map_object_type_id = mapObjectTypeId;
      marker.label = label || '';
      if (this.showActions) {
        marker.action_count = actionCount + childrenActionCount;
      } else {
        marker.action_count = 0;
      }
      marker.selected = (this.selected.filter(y => y.id === mapObjectId).length > 0);
      const iconData = this.getIconDataForType(mapObjectTypeId);
      const icon = this.getIconByMapObjectType(iconData, actionCount, label || '', marker.selected);
      marker.setIcon(icon);
      marker.on('click', (e) => {
        this.onPointClick(e, data);
      });
      marker.addTo(this.childrenLayer);
      if (marker.parent_id && addConnectingLine) {
        // const parent = this.mapObjects[marker.parent_id];
        const parent = dataCache.getMapObjectById(marker.parent_id);
        const parentCoords = this.getCoordinates(parent, 'point');
        if (parent && parentCoords !== null) {
          const polyline = this.createConnectingLine(coords, parentCoords);
          polyline.line_map_object_id = mapObjectId;
          polyline.parent_coords = parentCoords;
          polyline.addTo(this.childrenLayer);
        }
      }
    },
    async resetMap() {
      if (this.pointsLayer !== null) {
        this.pointsLayer.clearLayers();
        if (this.map.hasLayer(this.pointsLayer)) {
          this.map.removeLayer(this.pointsLayer);
        }
      }
      if (this.linesBackgroundLayer !== null) {
        this.linesBackgroundLayer.clearLayers();
        if (this.map.hasLayer(this.linesBackgroundLayer)) {
          this.map.removeLayer(this.linesBackgroundLayer);
        }
      }
      if (this.selectedLinesLayer !== null) {
        this.selectedLinesLayer.clearLayers();
        if (this.map.hasLayer(this.selectedLinesLayer)) {
          this.map.removeLayer(this.selectedLinesLayer);
        }
      }
      if (this.polygonsLayer !== null) {
        this.polygonsLayer.clearLayers();
        if (this.map.hasLayer(this.polygonsLayer)) {
          this.map.removeLayer(this.polygonsLayer);
        }
      }
      if (this.polygonPointsLayer !== null) {
        this.polygonPointsLayer.clearLayers();
        if (this.map.hasLayer(this.polygonPointsLayer)) {
          this.map.removeLayer(this.polygonPointsLayer);
        }
      }
      if (this.actionsLayer !== null) {
        this.actionsLayer.clearLayers();
        if (this.map.hasLayer(this.actionsLayer)) {
          this.map.removeLayer(this.actionsLayer);
        }
      }
      if (this.childrenLayer !== null) {
        this.childrenLayer.clearLayers();
        if (this.map.hasLayer(this.childrenLayer)) {
          this.map.removeLayer(this.childrenLayer);
        }
      }
      if (this.objectActionsLayer !== null) {
        this.objectActionsLayer.clearLayers();
        if (this.map.hasLayer(this.objectActionsLayer)) {
          this.map.removeLayer(this.objectActionsLayer);
        }
      }
      if (this.lineActionsLayer !== null) {
        this.lineActionsLayer.clearLayers();
        if (this.map.hasLayer(this.lineActionsLayer)) {
          this.map.removeLayer(this.lineActionsLayer);
        }
      }
      if (this.clustering) {
        this.pointsLayer = new L.MarkerClusterGroup({
          disableClusteringAtZoom: this.DISABLE_CLUSTERING_AT_ZOOM,
          maxClusterRadius: this.maxClusterRadius,
          chunkedLoading: true,
          showCoverageOnHover: false,
          removeOutsideVisibleBounds: true,
        });
        this.polygonPointsLayer = new L.MarkerClusterGroup({
          disableClusteringAtZoom: this.DISABLE_CLUSTERING_AT_ZOOM,
          maxClusterRadius: this.maxClusterRadius,
          chunkedLoading: true,
          showCoverageOnHover: false,
          removeOutsideVisibleBounds: true,
        });
      } else {
        this.pointsLayer = new L.MarkerClusterGroup({
          disableClusteringAtZoom: this.DISABLE_CLUSTERING_AT_ZOOM,
          maxClusterRadius: this.maxClusterRadiusForMultiSelect,
          chunkedLoading: true,
          showCoverageOnHover: false,
          removeOutsideVisibleBounds: true,
        });
        this.polygonPointsLayer = new L.LayerGroup();
      }
      this.linesBackgroundLayer = new L.LayerGroup();
      this.selectedLinesLayer = new L.LayerGroup();
      this.polygonsLayer = new L.LayerGroup();
      this.actionsLayer = new L.LayerGroup();
      this.childrenLayer = new L.LayerGroup();
      this.objectActionsLayer = new L.LayerGroup();
      this.lineActionsLayer = new L.LayerGroup();
      this.map.addLayer(this.polygonsLayer);
      this.map.addLayer(this.linesBackgroundLayer);
      this.map.addLayer(this.selectedLinesLayer);
      this.map.addLayer(this.polygonPointsLayer);
      this.map.addLayer(this.pointsLayer);
      if (this.showActions) {
        this.map.addLayer(this.actionsLayer);
        this.map.addLayer(this.objectActionsLayer);
        this.map.addLayer(this.lineActionsLayer);
      }
      if (this.showChildrenOnMap) {
        this.map.addLayer(this.childrenLayer);
      }
    },
    async clearMap() {
      this.pointsLayer.clearLayers();
      this.linesBackgroundLayer.clearLayers();
      this.selectedLinesLayer.clearLayers();
      this.polygonsLayer.clearLayers();
      this.polygonPointsLayer.clearLayers();
      this.actionsLayer.clearLayers();
      this.childrenLayer.clearLayers();
    },
    enableSelectAreaForMap() {
      this.map.selectArea.enable();
    },
    disableSelectAreaForMap() {
      this.map.selectArea.disable();
    },
    enableClusteringOnMap() {
      console.log('ObjectsLayer.enableClusteringOnMap()');
      this.clustering = true;
      this.map.setMinZoom(this.minZoom);
    },
    disableClusteringOnMap() {
      console.log('ObjectsLayer.disableClusteringOnMap()');
      this.clustering = false;
      const zoom = 14;
      this.minZoom = this.map.getMinZoom();
      this.map.off('moveend', this.onMoveEnd);

      if (this.map.getZoom() < zoom) {
        this.map.setZoom(zoom, { animate: false, duration: 0 });
      }
      this.map.setMinZoom(zoom);

      this.map.on('moveend', this.onMoveEnd);
    },
    showChildrenCluster() {
      console.log('ObjectsLayer.showChildrenCluster()');
      this.map.addLayer(this.childrenLayer);
      this.showChildrenOnMap = true;
    },
    hideChildrenCluster() {
      console.log('ObjectsLayer.hideChildrenCluster()');
      this.map.removeLayer(this.childrenLayer);
      this.showChildrenOnMap = false;
    },
    selectAreaOfObjects(bounds) {
      this.pointsLayer.eachLayer((point) => {
        if (bounds.contains(point.getLatLng())) {
          point.fire('click');
        }
      });
      this.findLinesInBounds(bounds);
      this.polygonsLayer.eachLayer((polygon) => {
        if (bounds.contains(polygon.getLatLngs())) {
          polygon.fire('click');
        }
      });
    },
    getIconDataForType(mapObjectTypeId) {
      if (this.mapObjectTypes === null || this.mapObjectTypes === undefined) {
        return null;
      }
      if (!Object.hasOwn(this.mapObjectTypes, mapObjectTypeId)) {
        return null;
      }
      if (!this.mapObjectTypes[mapObjectTypeId]) {
        return null;
      }
      if (!this.mapObjectTypes[mapObjectTypeId].icon) {
        return null;
      }
      return this.mapObjectTypes[mapObjectTypeId].icon.data;
    },
    zoomToMapObjects(mapObjects) {
      const objects = [];
      const mapObjectIds = mapObjects.map(item => item.id);
      mapObjectIds.forEach((mapObjectId) => {
        const point = this.pointsLayer.getLayer(this.pointsLayerMap[mapObjectId]);
        if (point) {
          objects.push(point);
        }
        const line = this.selectedLinesLayer.getLayer(this.selectedLinesLayerMap[mapObjectId]);
        if (line) {
          objects.push(line);
        }
        const polygon = this.polygonsLayer.getLayer(this.polygonsLayerMap[mapObjectId]);
        if (polygon) {
          objects.push(polygon);
        }
      });
      if (objects.length > 0) {
        const group = new L.FeatureGroup(objects);
        EventBus.$emit('Map.fitBounds', group.getBounds());
      }
    },
    async updateRelatedLines(mapObjectId, coordinates) {
      Object.values(await dataCache.getMapObjects()).forEach((mapObject) => {
        const type = this.mapObjectTypes[mapObject.map_object_type_id];
        if (!type) {
          return;
        }
        if (type.geo_type === GeoType.linestring) {
          const startPointId = this.getStartPointIdForLine(mapObject);
          if (startPointId === mapObjectId) {
            const newObject = mapObject;
            const coords = mapObject.geoObject.data.geometry.coordinates;
            coords[0] = coordinates;
            newObject.geoObject.data.geometry.coordinates = coords;
            this.updateMapObject({ mapObject: newObject, redraw: false });
          }
          const endPointId = this.getEndPointIdForLine(mapObject);
          if (endPointId === mapObjectId) {
            const newObject = mapObject;
            const coords = mapObject.geoObject.data.geometry.coordinates;
            coords[coords.length - 1] = coordinates;
            newObject.geoObject.data.geometry.coordinates = coords;
            this.updateMapObject({ mapObject: newObject, redraw: false });
          }
        }
      });
      this.redrawLinesOnMap();
    },
  },
  mounted() {
    if (!this.mapInitialized
      && this.settingsLoadedAt !== null
      && this.mapObjectsLoadedAt !== null) {
      console.log('ObjectsLayer.mounted()');
      this.initMap();
    }
    EventBus.$on('Map.redraw', () => {
      console.log('Map.redraw!');
      this.initMap();
    });
    EventBus.$on('Map.clearMap', () => {
      console.log('Map.clearMap!');
      this.clearMap();
    });
    EventBus.$on('Map.redrawMapObject', (mapObject) => {
      console.log('Map.redrawMapObject!', mapObject);
      const startTime = performance.now();
      if (this.geoTypeOfGeoObject(mapObject.geoObject) === 'LineString') {
        this.redrawLinesOnMap();
      } else {
        this.redrawMapObjectOnMap(mapObject, true);
      }
      const endTime = performance.now();
      console.log(`Map.redrawMapObject took ${endTime - startTime} milliseconds`);
    });
    EventBus.$on('Map.removeMapObjectFromMap', (mapObjectId) => {
      this.removeMapObjectFromMap(mapObjectId);
    });
    EventBus.$on('Map.redrawLinesOnMap', () => {
      console.log('Map.redrawLinesOnMap');
      this.redrawLinesOnMap();
    });
    EventBus.$on('Map.drawActionOnMap', (action) => {
      console.log('Map.drawActionOnMap!', action);
      const marker = this.createAction(action);
      marker.addTo(this.actionsLayer);
    });
    EventBus.$on('Map.redrawActionOnMap', (action) => {
      console.log('Map.redrawActionOnMap!', action);
      const actionIds = this.actionsOnMap().map(item => item.id);
      if (!actionIds.includes(action.id)) {
        this.removeActionFromMap(action);
      }
    });
    EventBus.$on('Map.removeActionFromMap', (action) => {
      console.log('Map.removeActionFromMap!', action);
      this.removeActionFromMap(action);
    });
    EventBus.$on('Map.enableSelectArea', () => {
      this.enableSelectAreaForMap();
    });
    EventBus.$on('Map.disableSelectArea', () => {
      this.disableSelectAreaForMap();
    });
    EventBus.$on('Map.enableClustering', () => {
      this.enableClusteringOnMap();
    });
    EventBus.$on('Map.disableClustering', () => {
      this.disableClusteringOnMap();
    });
    EventBus.$on('Map.showChildrenCluster', () => {
      this.showChildrenCluster();
    });
    EventBus.$on('Map.hideChildrenCluster', () => {
      this.hideChildrenCluster();
    });
    EventBus.$on('Map.zoomToMapObjects', (mapObjects) => {
      this.zoomToMapObjects(mapObjects);
    });
    EventBus.$on('Map.showActions', () => {
      console.log('Map.showActions');
      EventBus.$emit('Map.loading', true);
      const startTime = performance.now();
      this.showActions = true;
      this.map.addLayer(this.actionsLayer);
      this.map.addLayer(this.objectActionsLayer);
      this.map.addLayer(this.lineActionsLayer);
      this.redrawAllActionMapObjectsOnMap();
      const endTime = performance.now();
      EventBus.$emit('Map.loading', false);
      console.log(`Map.showActions took ${endTime - startTime} milliseconds`);
    });
    EventBus.$on('Map.hideActions', () => {
      console.log('Map.hideActions');
      EventBus.$emit('Map.loading', true);
      const startTime = performance.now();
      this.showActions = false;
      this.map.removeLayer(this.actionsLayer);
      this.map.removeLayer(this.objectActionsLayer);
      this.map.removeLayer(this.lineActionsLayer);
      this.redrawAllActionMapObjectsOnMap();
      const endTime = performance.now();
      EventBus.$emit('Map.loading', false);
      console.log(`Map.hideActions took ${endTime - startTime} milliseconds`);
    });
    EventBus.$on('Map.addChild', (mapObject, addConnectingLine = true) => {
      this.addChildToMap(mapObject, addConnectingLine);
    });
    EventBus.$on('Map.updateRelatedLines', (mapObjectId, coordinates) => {
      console.log('Map.updateRelatedLines');
      this.updateRelatedLines(mapObjectId, coordinates);
    });
    EventBus.$on('Map.setConnectedLines', (connectedLines) => {
      const lines = [];
      connectedLines.forEach((item) => {
        const line = JSON.parse(JSON.stringify(item));
        lines.push(line);
      });
      this.connectedLines = lines;
      lines.forEach((item) => {
        this.createLine(item);
      });
      this.redrawLinesOnMap();
    });
    EventBus.$on('Map.updateConnectedLines', (mapObjectId, coordinates) => {
      this.connectedLines.forEach((data) => {
        const mapObject = data;
        if (!mapObject.geoObject || !mapObject.geoObject.data || !mapObject.geoObject.data.properties) {
          return;
        }
        const coords = mapObject.geoObject.data.geometry.coordinates;
        const startPointId = this.getStartPointIdForLine(mapObject);
        const endPointId = this.getEndPointIdForLine(mapObject);
        if (startPointId === mapObjectId) {
          coords[0] = coordinates;
        } else if (endPointId === mapObjectId) {
          coords[coords.length - 1] = coordinates;
        }
        const line = this.selectedLinesLayer.getLayer(this.selectedLinesLayerMap[mapObject.id]);
        this.updateLine(line, mapObject);
      });
    });
    EventBus.$on('Map.cancelConnectedLines', (redrawLinesOnMap = true) => {
      this.connectedLines = [];
      this.selectedLinesLayer.clearLayers();
      this.selectedLinesLayerMap = {};
      if (redrawLinesOnMap) {
        this.redrawLinesOnMap();
      }
    });
  },
  beforeDestroy() {
    this.resetMap();
  },
  watch: {
    mapObjectsLoadedAt(newVal, oldVal) {
      if (oldVal === null
        && newVal !== null
        && this.settingsLoadedAt !== null
        && this.mapInitialized === false) {
        console.log('ObjectsLayer.mapObjectsLoadedAt()');
        this.initMap();
      }
    },
    settingsLoadedAt(newVal, oldVal) {
      if (oldVal === null
        && newVal !== null
        && this.mapObjectsLoadedAt !== null
        && this.mapInitialized === false) {
        console.log('ObjectsLayer.settingsLoadedAt()');
        this.initMap();
      }
    },
    selected() {
      console.log('update selected');
      const startTime = performance.now();
      this.setSelectedOnMap();
      const endTime = performance.now();
      console.log(`ObjectsLayer.selected watcher took ${endTime - startTime} milliseconds`);
    },
    async mapObject(newMapObject, oldMapObject) {
      console.log('ObjectsLayer.mapObject watcher');
      const startTime = performance.now();
      if (newMapObject !== null) {
        if (!this.allObjectsRelatedToSelected.includes(newMapObject.id)) {
          this.allObjectsRelatedToSelected.forEach((mapObjectId) => {
            const point = this.pointsLayer.getLayer(this.pointsLayerMap[mapObjectId]);
            if (point) {
              point.selected = false;
              this.updatePoint(point);
            }
          });

          const children = dataCache.getChildrenIdsForMapObjectId(newMapObject.id, true);
          children.push(newMapObject.id);
          this.allObjectsRelatedToSelected = children;
        }
        const startTime2 = performance.now();
        console.log(`related objects took ${startTime2 - startTime} milliseconds`);

        if (newMapObject.parent_id === null) {
          this.childrenLayer.clearLayers();

          this.getChildrenForMapObject({ id: newMapObject.id }).then((children) => {
            children.forEach((data) => {
              this.addChildToMap(data);
            });
            if (this.showActions) {
              this.redrawMapObjectOnMap(newMapObject);
              // if (oldMapObject) {
              //   this.redrawMapObjectOnMap(oldMapObject);
              // }
              // this.updateIconForMarker(newMapObject, true);
            }
          });
          const startTime3 = performance.now();
          console.log(`children took ${startTime3 - startTime2} milliseconds`);
          this.objectActionsLayer.clearLayers();
          this.drawObjectActionsOnMap(newMapObject);
          const startTime4 = performance.now();
          console.log(`actions took ${startTime4 - startTime3} milliseconds`);
        } else if (oldMapObject === null) {
          this.childrenLayer.clearLayers();

          let object = newMapObject;

          let type = this.mapObjectTypes[object.map_object_type_id];
          let hasOwnGeo = type ? type.mapObjectTypeSetting.data.find(setting => setting.name === 'hasOwnGeo') : false;
          while (!hasOwnGeo) {
            const parent = dataCache.getMapObjectById(object.parent_id);
            if (parent) {
              type = this.mapObjectTypes[parent.map_object_type_id];
              hasOwnGeo = type ? type.mapObjectTypeSetting.data.find(setting => setting.name === 'hasOwnGeo') : false;
              object = parent;
            } else {
              break;
            }
          }

          this.getChildrenForMapObject({ id: object.parent_id }).then((children) => {
            children.forEach((data) => {
              this.addChildToMap(data);
            });
          });
          const startTime3 = performance.now();
          console.log(`children2 took ${startTime3 - startTime2} milliseconds`);
        }
      } else {
        this.childrenLayer.clearLayers();
        this.objectActionsLayer.clearLayers();
        this.allObjectsRelatedToSelected.forEach((mapObjectId) => {
          const point = this.pointsLayer.getLayer(this.pointsLayerMap[mapObjectId]);
          if (point) {
            point.selected = false;
            this.updatePoint(point);
          }
        });
        this.allObjectsRelatedToSelected = [];
        if (oldMapObject !== null) {
          const point = this.pointsLayer.getLayer(this.pointsLayerMap[oldMapObject.id]);
          if (point) {
            point.selected = false;
            this.updatePoint(point);
          }
        }
        const startTime2 = performance.now();
        console.log(`else statement took ${startTime2 - startTime} milliseconds`);
      }
      const endTime = performance.now();
      console.log(`ObjectsLayer.mapObject watcher took ${endTime - startTime} milliseconds`);
    },
  },
};
</script>
<style>
.fade-in {
  opacity: 1;
  animation: fade 0.1s linear;
}

@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
